import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, KeyRound, Landmark, Trash2, Plus, Pencil, Check, X, Building2, Database, ChevronRight, Palette, ListOrdered, ShieldCheck, Wrench, ShieldAlert, Users, Inbox, FolderKanban, Sparkles, Package, Columns3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/lib/auth";
import api, { formatDetail } from "@/lib/api";
import { DIVISION_COLOR, DIVISIONS } from "@/lib/constants";
import { INDONESIAN_BANKS } from "@/lib/banks";
import Combobox from "@/components/Combobox";
import ConfirmDialog from "@/components/ConfirmDialog";
import StickyContextBar from "@/components/StickyContextBar";
import AppearanceSettingsForm from "@/components/AppearanceSettingsForm";
import NavOrderSettings from "@/components/NavOrderSettings";
import AssetCategorySettings from "@/components/AssetCategorySettings";
import AssetColumnSettings from "@/components/AssetColumnSettings";
import { useAccess } from "@/lib/access";
import { loadDisplayPrefs } from "@/lib/displayPrefs";
import { getExportColors, saveExportColors } from "@/lib/kasBankEstatement";

/**
 * SettingsPage — 2026-06-13 (user request): dirombak jadi TAB per fitur supaya
 * tiap area pengaturan mudah ditemukan & dikelola:
 *   Profil & Akun | Tampilan | Urutan Menu | Bank Perusahaan | Divisi |
 *   Master Data Keuangan | Sesi & Keamanan | Pemeliharaan Sistem
 * Tab admin hanya muncul sesuai hak akses user.
 */
export default function SettingsPage() {
  const { user } = useAuth();
  const [form, setForm] = useState({ old_password: "", new_password: "", confirm: "" });
  const [reveal, setReveal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [newAcc, setNewAcc] = useState({ bank_name: "", bank_account_no: "", opening_balance: "" });
  const [bankBusy, setBankBusy] = useState(false);
  const [editingBank, setEditingBank] = useState(null); // {id, bank_name, account_no}
  const [confirmDel, setConfirmDel] = useState({ open: false, id: null });

  // Sub-Divisions (Level 1 only)
  const [subDivs, setSubDivs] = useState([]);
  const [newSub, setNewSub] = useState({ division: "", name: "" });
  const [subBusy, setSubBusy] = useState(false);
  const [editingSub, setEditingSub] = useState(null); // {id, division, name}
  const [confirmDelSub, setConfirmDelSub] = useState({ open: false, id: null });

  // Preset Peran Staff (2026-06-19, user request) — custom presets CRUD.
  const [presets, setPresets] = useState([]);
  const [presetForm, setPresetForm] = useState({ label: "", description: "", division: "", sub_division: "", level: 3, base_preset_id: "junior_pm" });
  const [presetBusy, setPresetBusy] = useState(false);
  const [editingPreset, setEditingPreset] = useState(null); // {id, label, description, division, sub_division, level, base_preset_id}
  const [confirmDelPreset, setConfirmDelPreset] = useState({ open: false, id: null, label: "" });

  const canManageBank = user?.division === "BOD" || user?.division === "FAT";
  const canManageDivisions = user?.level === 1;
  const isBOD = user?.division === "BOD";
  const isSuperAdmin = isBOD && Number(user?.level) === 1;
  const canManageSecuritySession = isSuperAdmin;
  const { canPerm } = useAccess();
  // 2026-10-22 (user request): kelola Kategori Aset — user dgn izin update aset
  // (atau super admin) boleh menambah/menghapus kategori dari Pengaturan.
  const canManageAssetCat = isSuperAdmin || canPerm(user, "assets", "update");

  // Tampilan (appearance) prefs — untuk tab Tampilan.
  const [displayPrefs, setDisplayPrefs] = useState(null);
  useEffect(() => {
    let alive = true;
    loadDisplayPrefs().then((p) => { if (alive) setDisplayPrefs(p); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (canManageBank) {
      api.get("/company-bank-accounts")
        .then(({ data }) => setAccounts(data))
        .catch((e) => console.warn("Failed to load company bank accounts", e));
    }
  }, [canManageBank]);

  useEffect(() => {
    if (canManageDivisions) {
      api.get("/sub-divisions")
        .then(({ data }) => setSubDivs(data))
        .catch((e) => console.warn("Failed to load sub-divisions", e));
      api.get("/role-presets")
        .then(({ data }) => setPresets(data))
        .catch((e) => console.warn("Failed to load role presets", e));
    }
  }, [canManageDivisions]);

  // 149-cw-r4 (PROMPT 16-PRESENCE-SESSION-R1 A): Global idle-timeout setting.
  const [secSession, setSecSession] = useState({ idle_timeout_minutes: 30, warning_minutes: 2 });
  const [secBusy, setSecBusy] = useState(false);
  useEffect(() => {
    if (!user) return;
    api.get("/settings/security-session")
      .then(({ data }) => setSecSession({
        idle_timeout_minutes: data.idle_timeout_minutes,
        warning_minutes: data.warning_minutes,
      }))
      .catch(() => { /* keep defaults */ });
  }, [user]);
  const saveSecSession = async () => {
    setSecBusy(true);
    try {
      await api.put("/settings/security-session", secSession);
      toast.success(`Timeout disimpan (${secSession.idle_timeout_minutes} menit, warning ${secSession.warning_minutes} menit).`);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Gagal menyimpan.");
    } finally { setSecBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) {
      toast.error("New password and confirmation do not match");
      return;
    }
    if (form.new_password.length < 4) {
      toast.error("Password must be at least 4 characters");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        old_password: form.old_password,
        new_password: form.new_password,
      });
      toast.success("Password updated");
      setForm({ old_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const submitBank = async (e) => {
    e.preventDefault();
    if (!newAcc.bank_name.trim() || !newAcc.bank_account_no.trim()) {
      toast.error("Both bank name and account number are required");
      return;
    }
    setBankBusy(true);
    try {
      // 2026-06-20 (user request): saldo awal rekening (e-statement) ikut disimpan.
      const { data } = await api.post("/company-bank-accounts", {
        bank_name: newAcc.bank_name.trim(),
        bank_account_no: newAcc.bank_account_no.trim(),
        opening_balance: Number(newAcc.opening_balance) || 0,
      });
      setAccounts((a) => [...a, data]);
      setNewAcc({ bank_name: "", bank_account_no: "", opening_balance: "" });
      toast.success("Bank account added");
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally { setBankBusy(false); }
  };

  const removeBank = async (aid) => {
    try {
      await api.delete(`/company-bank-accounts/${aid}`);
      setAccounts((a) => a.filter((x) => x.id !== aid));
      toast.success("Removed");
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    }
  };
  const askRemoveBank = (aid) => setConfirmDel({ open: true, id: aid });

  const startEditBank = (a) => setEditingBank({ id: a.id, bank_name: a.bank_name, account_no: a.account_no, opening_balance: a.opening_balance ?? "" });
  const cancelEditBank = () => setEditingBank(null);
  const saveEditBank = async () => {
    if (!editingBank.bank_name.trim() || !editingBank.account_no.trim()) {
      toast.error("Bank name and account number are required");
      return;
    }
    try {
      const { data } = await api.patch(`/company-bank-accounts/${editingBank.id}`, {
        bank_name: editingBank.bank_name.trim(),
        bank_account_no: editingBank.account_no.trim(),
        opening_balance: Number(editingBank.opening_balance) || 0,
      });
      setAccounts((a) => a.map((x) => (x.id === editingBank.id ? data : x)));
      setEditingBank(null);
      toast.success("Bank account updated");
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    }
  };

  // ---- Preset Peran Staff (2026-06-19, user request) ----
  const submitPreset = async (e) => {
    e.preventDefault();
    if (!presetForm.label.trim() || !presetForm.division) {
      toast.error("Nama preset dan divisi wajib diisi");
      return;
    }
    setPresetBusy(true);
    try {
      const { data } = await api.post("/role-presets", {
        label: presetForm.label.trim(),
        description: presetForm.description.trim(),
        division: presetForm.division,
        sub_division: presetForm.sub_division || null,
        level: Number(presetForm.level),
        base_preset_id: presetForm.base_preset_id || null,
      });
      setPresets((xs) => [...xs, data]);
      setPresetForm({ label: "", description: "", division: "", sub_division: "", level: 3, base_preset_id: "junior_pm" });
      toast.success(`Preset "${data.label}" ditambahkan`);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally { setPresetBusy(false); }
  };
  const startEditPreset = (p) => setEditingPreset({ id: p.id, label: p.label, description: p.description || "", division: p.division, sub_division: p.sub_division || "", level: p.level, base_preset_id: p.base_preset_id || "" });
  const cancelEditPreset = () => setEditingPreset(null);
  const saveEditPreset = async () => {
    if (!editingPreset.label.trim() || !editingPreset.division) {
      toast.error("Nama preset dan divisi wajib diisi");
      return;
    }
    try {
      const { data } = await api.patch(`/role-presets/${editingPreset.id}`, {
        label: editingPreset.label.trim(),
        description: editingPreset.description.trim(),
        division: editingPreset.division,
        sub_division: editingPreset.sub_division || null,
        level: Number(editingPreset.level),
        base_preset_id: editingPreset.base_preset_id || null,
      });
      setPresets((xs) => xs.map((x) => (x.id === data.id ? data : x)));
      setEditingPreset(null);
      toast.success(`Preset "${data.label}" diupdate`);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    }
  };
  const askDelPreset = (p) => setConfirmDelPreset({ open: true, id: p.id, label: p.label });
  const doDelPreset = async (id) => {
    try {
      await api.delete(`/role-presets/${id}`);
      setPresets((xs) => xs.filter((x) => x.id !== id));
      toast.success("Preset dihapus");
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    }
  };

  // ---- Sub-Divisions ----
  const submitSub = async (e) => {
    e.preventDefault();
    if (!newSub.division || !newSub.name.trim()) {
      toast.error("Division and name are required");
      return;
    }
    setSubBusy(true);
    try {
      const { data } = await api.post("/sub-divisions", { division: newSub.division, name: newSub.name.trim() });
      setSubDivs((xs) => [...xs, data].sort((a, b) => a.division.localeCompare(b.division) || a.name.localeCompare(b.name)));
      setNewSub({ division: "", name: "" });
      toast.success("Sub-division added");
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally { setSubBusy(false); }
  };
  const startEditSub = (s) => setEditingSub({ id: s.id, division: s.division, name: s.name });
  const cancelEditSub = () => setEditingSub(null);
  const saveEditSub = async () => {
    if (!editingSub.division || !editingSub.name.trim()) {
      toast.error("Division and name are required");
      return;
    }
    try {
      await api.patch(`/sub-divisions/${editingSub.id}`, { division: editingSub.division, name: editingSub.name.trim() });
      setSubDivs((xs) => xs.map((x) => (x.id === editingSub.id ? { ...x, division: editingSub.division, name: editingSub.name.trim() } : x)));
      setEditingSub(null);
      toast.success("Sub-division updated (users cascaded)");
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    }
  };
  const askDelSub = (sid) => setConfirmDelSub({ open: true, id: sid });
  const doDelSub = async (sid) => {
    try {
      const { data } = await api.delete(`/sub-divisions/${sid}`);
      setSubDivs((xs) => xs.filter((x) => x.id !== sid));
      const n = data?.affected_users || 0;
      toast.success(n > 0 ? `Sub-divisi dihapus — ${n} user di-unassign otomatis` : "Sub-divisi dihapus");
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    }
  };

  // 2026-06-20 (user request): tab Settings dikelompokkan ke kategori sesuai sidebar
  // nav ("Task & Team", "Proyek", "SDM & Data Perusahaan", "Keuangan",
  // "Administrasi"). Tab tanpa isi menampilkan placeholder "menyusul".
  const TAB_GROUPS = [
    {
      category: "Personal",
      tabs: [
        { key: "account", label: "Profil & Akun", icon: KeyRound, show: true },
        { key: "appearance", label: "Tampilan", icon: Palette, show: true },
        { key: "nav-order", label: "Urutan Menu", icon: ListOrdered, show: true },
      ],
    },
    {
      category: "Task & Team",
      tabs: [
        { key: "task-team", label: "Task & Team", icon: Inbox, show: isSuperAdmin, placeholder: true },
      ],
    },
    {
      category: "Proyek",
      tabs: [
        { key: "projects", label: "Proyek", icon: FolderKanban, show: isSuperAdmin, placeholder: true },
      ],
    },
    {
      // R232: kategori mengikuti nama grup menu baru ("SDM").
      category: "SDM",
      tabs: [
        { key: "divisions", label: "Divisi & Sub-Divisi", icon: Building2, show: canManageDivisions },
      ],
    },
    {
      category: "Keuangan",
      tabs: [
        { key: "bank", label: "Bank Perusahaan", icon: Landmark, show: canManageBank },
        { key: "export-colors", label: "Warna Export", icon: Palette, show: canManageBank },
        { key: "finance-master", label: "Master Data Keuangan", icon: Database, show: isSuperAdmin },
      ],
    },
    {
      category: "Administrasi",
      tabs: [
        { key: "security", label: "Sesi & Keamanan", icon: ShieldCheck, show: canManageSecuritySession },
        { key: "asset-categories", label: "Kategori Aset", icon: Package, show: canManageAssetCat },
        { key: "asset-columns", label: "Tampilan Aset", icon: Columns3, show: canManageAssetCat },
        { key: "maintenance", label: "Pemeliharaan Sistem", icon: Wrench, show: isBOD },
      ],
    },
  ]
    .map((g) => ({ ...g, tabs: g.tabs.filter((t) => t.show) }))
    .filter((g) => g.tabs.length > 0);

  return (
    <div className="p-6 space-y-6">
      <div className="max-w-3xl">
        <p className="overline text-slate-500">Account</p>
        <h1 className="font-display text-3xl tracking-tight text-[#0B1D3A]">Settings</h1>
        <p className="text-sm text-slate-500 mt-1">Kelola akun, tampilan, urutan menu, dan pengaturan perusahaan — dikelompokkan per fitur.</p>
      </div>

      <StickyContextBar
        segments={["Account", "Settings"]}
        extra={<span className="text-slate-400 normal-case">{user?.division} · Lv{user?.level}</span>}
      />

      <Tabs defaultValue="account" className="max-w-5xl" data-testid="settings-tabs">
        {/* 2026-06-20: TabsList di-render sebagai grup visual per kategori sidebar. */}
        <TabsList className="flex flex-wrap h-auto justify-start rounded-none bg-slate-100 p-2 gap-x-4 gap-y-2">
          {TAB_GROUPS.map((g) => (
            <div key={g.category} className="flex flex-col gap-1" data-testid={`settings-cat-${g.category.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <div className="text-[9px] font-bold uppercase tracking-widest text-slate-500 px-1">{g.category}</div>
              <div className="flex flex-wrap gap-1">
                {g.tabs.map((t) => (
                  <TabsTrigger
                    key={t.key}
                    value={t.key}
                    data-testid={`settings-tab-${t.key}`}
                    className="rounded-none text-[11px] uppercase tracking-wider font-semibold data-[state=active]:bg-[#0B1D3A] data-[state=active]:text-white data-[state=active]:shadow-none"
                  >
                    <t.icon size={13} className="mr-1.5" /> {t.label}
                  </TabsTrigger>
                ))}
              </div>
            </div>
          ))}
        </TabsList>

        {/* ============ TAB PLACEHOLDER: TASK & TEAM (2026-09-25: admin-only, "Segera Hadir") ============ */}
        {isSuperAdmin && (
        <TabsContent value="task-team" data-testid="settings-tabpanel-task-team" className="mt-4">
          <SettingsPlaceholder icon={Inbox} title="Pengaturan Task & Team"
            description="Pengaturan default task (prioritas, deadline, notifikasi mention, dsb.) akan berada di sini." />
        </TabsContent>
        )}

        {/* ============ TAB PLACEHOLDER: PROYEK (2026-09-25: admin-only, "Segera Hadir") ============ */}
        {isSuperAdmin && (
        <TabsContent value="projects" data-testid="settings-tabpanel-projects" className="mt-4">
          <SettingsPlaceholder icon={FolderKanban} title="Pengaturan Proyek"
            description="Konfigurasi template project, kode default, tahap WO, dan retensi akan berada di sini." />
        </TabsContent>
        )}

        {/* ============ TAB: PROFIL & AKUN ============ */}
        <TabsContent value="account" data-testid="settings-tabpanel-account" className="space-y-6 mt-4">
          <section className="bg-white border border-slate-200 max-w-3xl">
            <header className="px-4 py-3 border-b border-slate-200">
              <h2 className="font-display text-lg tracking-tight">Profile</h2>
            </header>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 text-sm">
              <Field label="Name" value={user.name} />
              <Field label="Email" value={user.email} readonlyBadge="Cannot be changed" />
              <Field label="Division" value={<span className={`inline-block px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${DIVISION_COLOR[user.division]}`}>{user.division}</span>} />
              <Field label="Sub Division" value={user.sub_division} />
              <Field label="Access Level" value={<span className="inline-block px-1.5 py-0.5 text-[10px] font-semibold bg-slate-100 uppercase tracking-wider">Lv{user.level}</span>} />
            </div>
          </section>

          <section className="bg-white border border-slate-200 max-w-3xl">
            <header className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
              <KeyRound size={16} className="text-[#FF5A1F]" />
              <h2 className="font-display text-lg tracking-tight">Change Password</h2>
            </header>
            <form onSubmit={submit} className="p-5 space-y-4">
              <div className="space-y-1.5">
                <Label className="overline text-slate-600">Current Password</Label>
                <Input data-testid="cp-old" required type={reveal ? "text" : "password"} value={form.old_password} onChange={(e) => setForm({ ...form, old_password: e.target.value })} className="rounded-none" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="overline text-slate-600">New Password</Label>
                  <Input data-testid="cp-new" required type={reveal ? "text" : "password"} value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} className="rounded-none" />
                </div>
                <div className="space-y-1.5">
                  <Label className="overline text-slate-600">Confirm New Password</Label>
                  <Input data-testid="cp-confirm" required type={reveal ? "text" : "password"} value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className="rounded-none" />
                </div>
              </div>
              <div className="flex items-center justify-between pt-2">
                <button type="button" data-testid="cp-reveal" onClick={() => setReveal((v) => !v)} className="text-xs uppercase tracking-wider text-slate-600 hover:text-[#0B1D3A] flex items-center gap-1">
                  {reveal ? <EyeOff size={14} /> : <Eye size={14} />} {reveal ? "Hide" : "Show"} passwords
                </button>
                <Button data-testid="cp-submit" type="submit" disabled={busy} className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13] uppercase text-xs tracking-wider font-semibold">
                  {busy ? "Saving…" : "Update Password"}
                </Button>
              </div>
            </form>
          </section>
        </TabsContent>

        {/* ============ TAB: TAMPILAN (APPEARANCE) ============ */}
        <TabsContent value="appearance" data-testid="settings-tabpanel-appearance" className="mt-4">
          <section className="bg-white border border-slate-200 max-w-3xl">
            <header className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
              <Palette size={16} className="text-[#0B1D3A]" />
              <h2 className="font-display text-lg tracking-tight">Tampilan & Perilaku Sidebar</h2>
              <span className="text-[10px] text-slate-400 uppercase tracking-wider bg-slate-100 px-1.5 py-0.5 ml-1">Per akun Anda</span>
            </header>
            <div className="p-5">
              {displayPrefs ? (
                <AppearanceSettingsForm prefs={displayPrefs} onSaved={setDisplayPrefs} />
              ) : (
                <div className="text-xs text-slate-400" data-testid="appearance-loading">Memuat preferensi…</div>
              )}
            </div>
          </section>
        </TabsContent>

        {/* ============ TAB: URUTAN MENU ============ */}
        <TabsContent value="nav-order" data-testid="settings-tabpanel-nav-order" className="mt-4">
          <section className="bg-white border border-slate-200 max-w-3xl" data-testid="nav-order-section">
            <header className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
              <ListOrdered size={16} className="text-[#0B1D3A]" />
              <h2 className="font-display text-lg tracking-tight">Urutan Menu Sidebar</h2>
              {isSuperAdmin ? (
                <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 ml-1">Super Admin → Default Global</span>
              ) : (
                <span className="text-[10px] text-slate-400 uppercase tracking-wider bg-slate-100 px-1.5 py-0.5 ml-1">Per akun Anda</span>
              )}
            </header>
            <div className="p-5">
              <NavOrderSettings isSuperAdmin={isSuperAdmin} />
            </div>
          </section>
        </TabsContent>

        {/* ============ TAB: BANK PERUSAHAAN (BOD & FAT) ============ */}
        {canManageBank && (
          <TabsContent value="bank" data-testid="settings-tabpanel-bank" className="mt-4">
            <section className="bg-white border border-slate-200 max-w-3xl" data-testid="bank-section">
              <header className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
                <Landmark size={16} className="text-[#0B1D3A]" />
                <h2 className="font-display text-lg tracking-tight">Company Bank Accounts</h2>
                <span className="text-[10px] text-slate-400 uppercase tracking-wider bg-slate-100 px-1.5 py-0.5 ml-1">{user.division} only</span>
              </header>
              <div className="p-5 space-y-4">
                <p className="text-xs text-slate-500">Manage the list of company bank accounts. These are selectable when you record income on a project.</p>
                <div className="border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11px] text-emerald-800 flex items-start gap-2">
                  <Landmark size={14} className="text-emerald-700 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-semibold">Bank ini juga jadi acuan &quot;fee-free&quot;</div>
                    <div className="text-emerald-700/90">Rekening personil karyawan yang menggunakan salah satu nama bank di daftar ini tidak akan menampilkan warning &quot;biaya admin transfer&quot;. Bank di luar daftar akan menampilkan warning + minta persetujuan sebelum disimpan.</div>
                  </div>
                </div>

                {accounts.length > 0 && (
                  <div className="border border-slate-200">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr className="text-left">{["Bank", "Account Number", "Saldo Awal", ""].map((h) => <th key={h} className="px-3 py-2 overline text-slate-600">{h}</th>)}</tr>
                      </thead>
                      <tbody data-testid="bank-accounts-list">
                        {accounts.map((a) => {
                          const isEditing = editingBank?.id === a.id;
                          return (
                            <tr key={a.id} className="border-b border-slate-100">
                              <td className="px-3 py-2 font-medium text-[#0B1D3A]">
                                {isEditing ? (
                                  <Combobox
                                    testid={`bank-edit-name-${a.id}`}
                                    value={editingBank.bank_name}
                                    onChange={(v) => setEditingBank({ ...editingBank, bank_name: v })}
                                    options={INDONESIAN_BANKS}
                                    placeholder="Pilih bank…"
                                  />
                                ) : a.bank_name}
                              </td>
                              <td className="px-3 py-2 font-mono">
                                {isEditing ? (
                                  <Input
                                    data-testid={`bank-edit-account-${a.id}`}
                                    value={editingBank.account_no}
                                    onChange={(e) => setEditingBank({ ...editingBank, account_no: e.target.value })}
                                    className="rounded-none h-8 font-mono"
                                  />
                                ) : a.account_no}
                              </td>
                              <td className="px-3 py-2 tabular-nums text-right" data-testid={`bank-opening-${a.id}`}>
                                {isEditing ? (
                                  <Input
                                    data-testid={`bank-edit-opening-${a.id}`}
                                    type="number" inputMode="numeric"
                                    value={editingBank.opening_balance}
                                    onChange={(e) => setEditingBank({ ...editingBank, opening_balance: e.target.value })}
                                    className="rounded-none h-8 font-mono text-right"
                                    placeholder="0"
                                  />
                                ) : `Rp ${Number(a.opening_balance || 0).toLocaleString("id-ID")}`}
                              </td>
                              <td className="px-3 py-2 text-right">
                                {isEditing ? (
                                  <div className="inline-flex gap-1">
                                    <button data-testid={`bank-save-${a.id}`} onClick={saveEditBank} className="text-emerald-600 hover:text-emerald-800 p-1" title="Simpan"><Check size={14} /></button>
                                    <button data-testid={`bank-cancel-${a.id}`} onClick={cancelEditBank} className="text-slate-500 hover:text-slate-700 p-1" title="Batal"><X size={14} /></button>
                                  </div>
                                ) : (
                                  <div className="inline-flex gap-1">
                                    <button data-testid={`bank-edit-${a.id}`} onClick={() => startEditBank(a)} className="text-slate-500 hover:text-[#0B1D3A] p-1" title="Edit"><Pencil size={13} /></button>
                                    <button data-testid={`bank-del-${a.id}`} onClick={() => askRemoveBank(a.id)} className="text-rose-500 hover:text-rose-700 p-1" title="Delete"><Trash2 size={14} /></button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                <form onSubmit={submitBank} className="border border-dashed border-slate-300 p-4 grid grid-cols-1 md:grid-cols-6 gap-3">
                  <div className="md:col-span-2 space-y-1.5">
                    <Label className="overline text-slate-600">Bank Name</Label>
                    <Combobox
                      testid="bank-name"
                      value={newAcc.bank_name}
                      onChange={(v) => setNewAcc({ ...newAcc, bank_name: v })}
                      options={INDONESIAN_BANKS}
                      placeholder="Pilih dari daftar bank Indonesia…"
                    />
                  </div>
                  <div className="md:col-span-2 space-y-1.5">
                    <Label className="overline text-slate-600">Account Number</Label>
                    <Input data-testid="bank-account" required value={newAcc.bank_account_no} onChange={(e) => setNewAcc({ ...newAcc, bank_account_no: e.target.value })} placeholder="1234567890" className="rounded-none font-mono" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="overline text-slate-600">Saldo Awal</Label>
                    <Input data-testid="bank-opening-input" type="number" inputMode="numeric" value={newAcc.opening_balance} onChange={(e) => setNewAcc({ ...newAcc, opening_balance: e.target.value })} placeholder="0" className="rounded-none font-mono text-right" />
                  </div>
                  <div className="flex items-end">
                    <Button data-testid="bank-submit" type="submit" disabled={bankBusy} className="w-full rounded-none bg-[#0B1D3A] hover:bg-[#1a2e52] uppercase text-xs tracking-wider font-semibold">
                      <Plus size={14} className="mr-1" /> {bankBusy ? "…" : "Add"}
                    </Button>
                  </div>
                </form>
              </div>
            </section>
          </TabsContent>
        )}

        {/* ============ TAB: DIVISI & SUB-DIVISI (Level 1) ============ */}
        {canManageDivisions && (
          <TabsContent value="divisions" data-testid="settings-tabpanel-divisions" className="mt-4">
            <section className="bg-white border border-slate-200 max-w-3xl" data-testid="subdivisions-section">
              <header className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
                <Building2 size={16} className="text-[#FF5A1F]" />
                <h2 className="font-display text-lg tracking-tight">Divisions & Sub-Divisions</h2>
                <span className="text-[10px] text-slate-400 uppercase tracking-wider bg-slate-100 px-1.5 py-0.5 ml-1">Level 1 only</span>
              </header>
              <div className="p-5 space-y-4">
                <p className="text-xs text-slate-500">
                  Manage sub-divisions under each main division. Renaming cascades to all users assigned to that sub-division. Menghapus sub-divisi akan otomatis meng-unassign semua user yang masih menggunakannya.
                </p>

                {subDivs.length > 0 && (
                  <div className="border border-slate-200">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr className="text-left">{["Division", "Sub-Division", ""].map((h) => <th key={h} className="px-3 py-2 overline text-slate-600">{h}</th>)}</tr>
                      </thead>
                      <tbody data-testid="subdivisions-list">
                        {subDivs.map((s) => {
                          const isEd = editingSub?.id === s.id;
                          return (
                            <tr key={s.id} className="border-b border-slate-100">
                              <td className="px-3 py-2">
                                {isEd ? (
                                  <Select value={editingSub.division} onValueChange={(v) => setEditingSub({ ...editingSub, division: v })}>
                                    <SelectTrigger data-testid={`sub-edit-division-${s.id}`} className="rounded-none h-8"><SelectValue /></SelectTrigger>
                                    <SelectContent className="rounded-none">
                                      {DIVISIONS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  <span className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${DIVISION_COLOR[s.division] || "bg-slate-600"}`}>{s.division}</span>
                                )}
                              </td>
                              <td className="px-3 py-2 font-medium text-[#0B1D3A]">
                                {isEd ? (
                                  <Input
                                    data-testid={`sub-edit-name-${s.id}`}
                                    value={editingSub.name}
                                    onChange={(e) => setEditingSub({ ...editingSub, name: e.target.value })}
                                    className="rounded-none h-8"
                                  />
                                ) : s.name}
                              </td>
                              <td className="px-3 py-2 text-right">
                                {isEd ? (
                                  <div className="inline-flex gap-1">
                                    <button data-testid={`sub-save-${s.id}`} onClick={saveEditSub} className="text-emerald-600 hover:text-emerald-800 p-1" title="Simpan"><Check size={14} /></button>
                                    <button data-testid={`sub-cancel-${s.id}`} onClick={cancelEditSub} className="text-slate-500 hover:text-slate-700 p-1" title="Batal"><X size={14} /></button>
                                  </div>
                                ) : (
                                  <div className="inline-flex gap-1">
                                    <button data-testid={`sub-edit-${s.id}`} onClick={() => startEditSub(s)} className="text-slate-500 hover:text-[#0B1D3A] p-1" title="Edit"><Pencil size={13} /></button>
                                    <button data-testid={`sub-del-${s.id}`} onClick={() => askDelSub(s.id)} className="text-rose-500 hover:text-rose-700 p-1" title="Delete"><Trash2 size={14} /></button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                <form onSubmit={submitSub} className="border border-dashed border-slate-300 p-4 grid grid-cols-1 md:grid-cols-5 gap-3">
                  <div className="md:col-span-2 space-y-1.5">
                    <Label className="overline text-slate-600">Division</Label>
                    <Select value={newSub.division} onValueChange={(v) => setNewSub({ ...newSub, division: v })}>
                      <SelectTrigger data-testid="sub-new-division" className="rounded-none"><SelectValue placeholder="Choose division…" /></SelectTrigger>
                      <SelectContent className="rounded-none">
                        {DIVISIONS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2 space-y-1.5">
                    <Label className="overline text-slate-600">Sub-Division Name</Label>
                    <Input data-testid="sub-new-name" required value={newSub.name} onChange={(e) => setNewSub({ ...newSub, name: e.target.value })} placeholder="e.g. P. Batam, Finance" className="rounded-none" />
                  </div>
                  <div className="flex items-end">
                    <Button data-testid="sub-submit" type="submit" disabled={subBusy} className="w-full rounded-none bg-[#0B1D3A] hover:bg-[#1a2e52] uppercase text-xs tracking-wider font-semibold">
                      <Plus size={14} className="mr-1" /> {subBusy ? "…" : "Add"}
                    </Button>
                  </div>
                </form>
              </div>
            </section>

            {/* ===== Preset Peran Staff (2026-06-19, user request) ===== */}
            <section className="bg-white border border-slate-200 max-w-3xl mt-6" data-testid="presets-section">
              <header className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
                <Users size={16} className="text-[#0B1D3A]" />
                <h2 className="font-display text-lg tracking-tight">Preset Peran Staff</h2>
                <span className="text-[10px] text-slate-400 uppercase tracking-wider bg-slate-100 px-1.5 py-0.5 ml-1">mempercepat tambah staff</span>
              </header>
              <div className="p-5 space-y-4">
                <p className="text-xs text-slate-500">
                  Preset mengisi otomatis <b>divisi + level</b> (dan sub-divisi bila diatur) saat membuat akun staff baru, sekaligus men-seed permission matrix dari <b>preset dasar</b>. Preset <b>Bawaan</b> read-only; preset <b>Custom</b> bisa diedit/dihapus.
                </p>

                {presets.length > 0 && (
                  <div className="border border-slate-200">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr className="text-left">{["Preset", "Divisi", "Sub-Divisi", "Level", "Dasar Permission", ""].map((h) => <th key={h} className="px-3 py-2 overline text-slate-600">{h}</th>)}</tr>
                      </thead>
                      <tbody data-testid="presets-list">
                        {presets.map((p) => {
                          const isEd = editingPreset?.id === p.id;
                          return (
                            <tr key={p.id} className="border-b border-slate-100" data-testid={`preset-row-${p.id}`}>
                              <td className="px-3 py-2 font-medium text-[#0B1D3A]">
                                {isEd ? (
                                  <div className="space-y-1">
                                    <Input data-testid={`preset-edit-label-${p.id}`} value={editingPreset.label} onChange={(e) => setEditingPreset({ ...editingPreset, label: e.target.value })} className="rounded-none h-8" />
                                    <Input data-testid={`preset-edit-desc-${p.id}`} value={editingPreset.description} onChange={(e) => setEditingPreset({ ...editingPreset, description: e.target.value })} placeholder="Deskripsi singkat…" className="rounded-none h-8 text-xs" />
                                  </div>
                                ) : (
                                  <>
                                    {p.label}
                                    {!p.custom && <span className="ml-1.5 text-[9px] uppercase tracking-wider bg-slate-100 text-slate-500 px-1 py-0.5">Bawaan</span>}
                                    {p.custom && <span className="ml-1.5 text-[9px] uppercase tracking-wider bg-emerald-50 border border-emerald-300 text-emerald-700 px-1 py-0.5">Custom</span>}
                                    {p.description && <div className="text-[10px] text-slate-500 font-normal mt-0.5">{p.description}</div>}
                                  </>
                                )}
                              </td>
                              <td className="px-3 py-2">
                                {isEd ? (
                                  <Select value={editingPreset.division} onValueChange={(v) => setEditingPreset({ ...editingPreset, division: v, sub_division: "" })}>
                                    <SelectTrigger data-testid={`preset-edit-division-${p.id}`} className="rounded-none h-8"><SelectValue /></SelectTrigger>
                                    <SelectContent className="rounded-none">{DIVISIONS.map((dv) => <SelectItem key={dv} value={dv}>{dv}</SelectItem>)}</SelectContent>
                                  </Select>
                                ) : <span className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${DIVISION_COLOR[p.division] || "bg-slate-600 text-white"}`}>{p.division}</span>}
                              </td>
                              <td className="px-3 py-2 text-xs text-slate-600">
                                {isEd ? (
                                  <Select value={editingPreset.sub_division || "__none__"} onValueChange={(v) => setEditingPreset({ ...editingPreset, sub_division: v === "__none__" ? "" : v })}>
                                    <SelectTrigger data-testid={`preset-edit-sub-${p.id}`} className="rounded-none h-8"><SelectValue placeholder="—" /></SelectTrigger>
                                    <SelectContent className="rounded-none">
                                      <SelectItem value="__none__">—</SelectItem>
                                      {subDivs.filter((s) => s.division === editingPreset.division).map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                ) : (p.sub_division || <span className="text-slate-300">—</span>)}
                              </td>
                              <td className="px-3 py-2">
                                {isEd ? (
                                  <Select value={String(editingPreset.level)} onValueChange={(v) => setEditingPreset({ ...editingPreset, level: Number(v) })}>
                                    <SelectTrigger data-testid={`preset-edit-level-${p.id}`} className="rounded-none h-8"><SelectValue /></SelectTrigger>
                                    <SelectContent className="rounded-none">{[1, 2, 3, 4, 5].map((l) => <SelectItem key={l} value={String(l)}>Lv{l}</SelectItem>)}</SelectContent>
                                  </Select>
                                ) : <span className="inline-block px-1.5 py-0.5 text-[10px] font-semibold bg-slate-100 uppercase tracking-wider">Lv{p.level}</span>}
                              </td>
                              <td className="px-3 py-2 text-[10px] text-slate-500">
                                {isEd ? (
                                  <Select value={editingPreset.base_preset_id || "__none__"} onValueChange={(v) => setEditingPreset({ ...editingPreset, base_preset_id: v === "__none__" ? "" : v })}>
                                    <SelectTrigger data-testid={`preset-edit-base-${p.id}`} className="rounded-none h-8"><SelectValue placeholder="Kosong" /></SelectTrigger>
                                    <SelectContent className="rounded-none">
                                      <SelectItem value="__none__">— Tanpa dasar (kosong) —</SelectItem>
                                      {presets.filter((x) => x.id !== p.id).map((x) => <SelectItem key={x.id} value={x.id}>{x.label}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                ) : (p.base_preset_id ? (presets.find((x) => x.id === p.base_preset_id)?.label || p.base_preset_id) : <span className="text-slate-300">—</span>)}
                              </td>
                              <td className="px-3 py-2 text-right">
                                {isEd ? (
                                  <div className="inline-flex gap-1">
                                    <button data-testid={`preset-save-${p.id}`} onClick={saveEditPreset} className="text-emerald-600 hover:text-emerald-800 p-1" title="Simpan"><Check size={14} /></button>
                                    <button data-testid={`preset-cancel-${p.id}`} onClick={cancelEditPreset} className="text-slate-500 hover:text-slate-700 p-1" title="Batal"><X size={14} /></button>
                                  </div>
                                ) : (
                                  <div className="inline-flex gap-1">
                                    {/* 2026-06-19 (user request): preset bawaan juga bisa dihapus. */}
                                    {p.custom && (
                                      <button data-testid={`preset-edit-${p.id}`} onClick={() => startEditPreset(p)} className="text-slate-500 hover:text-[#0B1D3A] p-1" title="Edit"><Pencil size={13} /></button>
                                    )}
                                    <button data-testid={`preset-del-${p.id}`} onClick={() => askDelPreset(p)} className="text-rose-500 hover:text-rose-700 p-1" title="Hapus preset"><Trash2 size={14} /></button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                <form onSubmit={submitPreset} className="border border-dashed border-slate-300 p-4 grid grid-cols-1 md:grid-cols-5 gap-3">
                  <div className="md:col-span-2 space-y-1.5">
                    <Label className="overline text-slate-600">Nama Preset</Label>
                    <Input data-testid="preset-new-label" required value={presetForm.label} onChange={(e) => setPresetForm({ ...presetForm, label: e.target.value })} placeholder='cth: "Staff Proyek", "Staff Kantor", "Guest"' className="rounded-none" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="overline text-slate-600">Divisi</Label>
                    <Select value={presetForm.division} onValueChange={(v) => setPresetForm({ ...presetForm, division: v, sub_division: "" })}>
                      <SelectTrigger data-testid="preset-new-division" className="rounded-none"><SelectValue placeholder="Pilih…" /></SelectTrigger>
                      <SelectContent className="rounded-none">{DIVISIONS.map((dv) => <SelectItem key={dv} value={dv}>{dv}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="overline text-slate-600">Sub-Divisi</Label>
                    <Select value={presetForm.sub_division || "__none__"} onValueChange={(v) => setPresetForm({ ...presetForm, sub_division: v === "__none__" ? "" : v })} disabled={!presetForm.division}>
                      <SelectTrigger data-testid="preset-new-sub" className="rounded-none"><SelectValue placeholder={presetForm.division ? "— opsional —" : "Pilih divisi dulu"} /></SelectTrigger>
                      <SelectContent className="rounded-none">
                        <SelectItem value="__none__">— opsional —</SelectItem>
                        {subDivs.filter((s) => s.division === presetForm.division).map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="overline text-slate-600">Level</Label>
                    <Select value={String(presetForm.level)} onValueChange={(v) => setPresetForm({ ...presetForm, level: Number(v) })}>
                      <SelectTrigger data-testid="preset-new-level" className="rounded-none"><SelectValue /></SelectTrigger>
                      <SelectContent className="rounded-none">{[1, 2, 3, 4, 5].map((l) => <SelectItem key={l} value={String(l)}>Lv{l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-5 grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="space-y-1.5">
                      <Label className="overline text-slate-600">Deskripsi <span className="text-slate-400 normal-case">(opsional)</span></Label>
                      <Input data-testid="preset-new-desc" value={presetForm.description} onChange={(e) => setPresetForm({ ...presetForm, description: e.target.value })} placeholder="cth: Staff lapangan proyek — akses dasar saja" className="rounded-none" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="overline text-slate-600">Salin Permission Dari (preset dasar)</Label>
                      <Select value={presetForm.base_preset_id || "__none__"} onValueChange={(v) => setPresetForm({ ...presetForm, base_preset_id: v === "__none__" ? "" : v })}>
                        <SelectTrigger data-testid="preset-new-base" className="rounded-none"><SelectValue /></SelectTrigger>
                        <SelectContent className="rounded-none">
                          <SelectItem value="__none__">— Tanpa dasar (permission kosong) —</SelectItem>
                          {presets.map((x) => <SelectItem key={x.id} value={x.id}>{x.label} · {x.division} Lv{x.level}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    {/* 2026-06-19 fix: tombol dipindah ke baris bawah + nowrap supaya
                        teks "Tambah Preset" tidak terpotong. */}
                    <div className="flex items-end">
                      <Button data-testid="preset-new-submit" type="submit" disabled={presetBusy}
                        className="w-full rounded-none bg-[#0B1D3A] hover:bg-[#1a2e52] uppercase text-xs tracking-wider font-semibold whitespace-nowrap">
                        <Plus size={14} className="mr-1" /> {presetBusy ? "…" : "Tambah Preset"}
                      </Button>
                    </div>
                  </div>
                </form>
              </div>
            </section>
          </TabsContent>
        )}

        {/* ============ TAB: WARNA EXPORT (BOD & FAT) ============ */}
        {canManageBank && (
          <TabsContent value="export-colors" data-testid="settings-tabpanel-export-colors" className="mt-4">
            <ExportColorsSettings />
          </TabsContent>
        )}

        {/* ============ TAB: MASTER DATA KEUANGAN (BOD Lv1) ============ */}
        {isSuperAdmin && (
          <TabsContent value="finance-master" data-testid="settings-tabpanel-finance-master" className="mt-4">
            <section className="bg-white border border-slate-200 max-w-3xl" data-testid="finance-master-link-section">
              <header className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
                <Database size={16} className="text-[#0B1D3A]" />
                <h2 className="font-display text-lg tracking-tight">Master Data Keuangan</h2>
                <span className="text-[10px] text-slate-400 uppercase tracking-wider bg-slate-100 px-1.5 py-0.5 ml-1">BOD Lv1 only</span>
              </header>
              <div className="p-5">
                <p className="text-xs text-slate-500 mb-3">Kelola daftar <strong>Akun Piutang</strong> &amp; <strong>Pusat Biaya</strong> yang dipakai sebagai referensi akuntansi di detail invoice Laporan Keuangan.</p>
                <Link to="/settings/finance-master" data-testid="link-finance-master"
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#0B1D3A] hover:bg-[#122a4d] text-white text-xs uppercase tracking-wider font-semibold">
                  Buka Master Data <ChevronRight size={13} />
                </Link>
              </div>
            </section>
          </TabsContent>
        )}

        {/* ============ TAB: SESI & KEAMANAN GLOBAL (BOD Lv1) ============ */}
        {canManageSecuritySession && (
          <TabsContent value="security" data-testid="settings-tabpanel-security" className="mt-4">
            <section className="bg-white border border-slate-200 max-w-3xl" data-testid="security-session-section">
              <div className="border-b border-slate-200 px-4 py-3 flex items-center gap-2 bg-slate-50">
                <KeyRound size={14} className="text-[#0B1D3A]" />
                <h2 className="font-display text-lg tracking-tight text-[#0B1D3A]">Security & Session</h2>
                <span className="ml-auto text-[10px] uppercase tracking-wider text-slate-500">BOD Lv1 Only</span>
              </div>
              <div className="p-4 space-y-3">
                <p className="text-xs text-slate-600">
                  Batas waktu tidak aktif berlaku global (termasuk BOD Lv1). Aktivitas nyata user (klik, ketik, scroll, navigasi) memperpanjang sesi;
                  heartbeat &amp; polling latar belakang TIDAK memperpanjang. Perubahan setting berlaku dengan grace-period 5 menit.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-slate-600">Batas Waktu Tidak Aktif</Label>
                    <select
                      data-testid="input-idle-timeout"
                      value={secSession.idle_timeout_minutes}
                      onChange={(e) => {
                        const v = e.target.value === "custom" ? secSession.idle_timeout_minutes : Number(e.target.value);
                        setSecSession((s) => ({ ...s, idle_timeout_minutes: v }));
                      }}
                      className="w-full mt-1 border border-slate-300 px-2 py-1.5 text-xs"
                    >
                      {[15, 30, 60, 120, 240].map((m) => (
                        <option key={m} value={m}>{m < 60 ? `${m} menit` : `${m/60} jam`}</option>
                      ))}
                      <option value={secSession.idle_timeout_minutes}>Custom ({secSession.idle_timeout_minutes} menit)</option>
                    </select>
                    <input
                      type="number"
                      data-testid="input-idle-timeout-custom"
                      min="5" max="1440"
                      value={secSession.idle_timeout_minutes}
                      onChange={(e) => setSecSession((s) => ({ ...s, idle_timeout_minutes: Math.max(5, Math.min(1440, Number(e.target.value) || 30)) }))}
                      className="w-full mt-1 border border-slate-200 px-2 py-1 text-[11px]"
                      placeholder="Custom (5-1440 menit)"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-slate-600">Peringatan (menit sebelum logout)</Label>
                    <input
                      type="number"
                      data-testid="input-warning-minutes"
                      min="1" max={secSession.idle_timeout_minutes - 1}
                      value={secSession.warning_minutes}
                      onChange={(e) => setSecSession((s) => ({ ...s, warning_minutes: Math.max(1, Math.min(s.idle_timeout_minutes - 1, Number(e.target.value) || 2)) }))}
                      className="w-full mt-1 border border-slate-300 px-2 py-1.5 text-xs"
                    />
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      Harus kurang dari batas waktu tidak aktif.
                    </div>
                  </div>
                </div>
                <Button
                  data-testid="btn-save-security-session"
                  onClick={saveSecSession}
                  disabled={secBusy || secSession.warning_minutes >= secSession.idle_timeout_minutes}
                  className="rounded-none bg-[#0B1D3A] hover:bg-[#082033] text-white uppercase text-xs tracking-wider font-semibold"
                >
                  {secBusy ? "Menyimpan…" : "Simpan Pengaturan"}
                </Button>
              </div>
            </section>
          </TabsContent>
        )}

        {/* ============ TAB: KATEGORI ASET (2026-10-22, user request) ============ */}
        {canManageAssetCat && (
          <TabsContent value="asset-categories" data-testid="settings-tabpanel-asset-categories" className="mt-4">
            <AssetCategorySettings />
          </TabsContent>
        )}

        {/* 2026-10-22 (user request): default kolom tabel Inventaris & Aset. */}
        {canManageAssetCat && (
          <TabsContent value="asset-columns" data-testid="settings-tabpanel-asset-columns" className="mt-4">
            <AssetColumnSettings />
          </TabsContent>
        )}

        {/* ============ TAB: PEMELIHARAAN SISTEM (BOD) ============ */}
        {isBOD && (
          <TabsContent value="maintenance" data-testid="settings-tabpanel-maintenance" className="space-y-6 mt-4">
            <section className="bg-white border border-slate-200 max-w-3xl" data-testid="data-integrity-link-section">
              <header className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
                <ShieldAlert size={16} className="text-[#0B1D3A]" />
                <h2 className="font-display text-lg tracking-tight">Integritas Data</h2>
                <span className="text-[10px] text-slate-400 uppercase tracking-wider bg-slate-100 px-1.5 py-0.5 ml-1">BOD only</span>
              </header>
              <div className="p-5">
                <p className="text-xs text-slate-500 mb-3">Alat audit & perbaikan konsistensi data antar modul (proyek, WO, keuangan).</p>
                <Link to="/admin/data-integrity" data-testid="link-data-integrity"
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#0B1D3A] hover:bg-[#122a4d] text-white text-xs uppercase tracking-wider font-semibold">
                  Buka Integritas Data <ChevronRight size={13} />
                </Link>
              </div>
            </section>

          </TabsContent>
        )}
      </Tabs>

      <ConfirmDialog
        open={confirmDel.open}
        onOpenChange={(o) => setConfirmDel({ ...confirmDel, open: o })}
        title="Remove bank account?"
        description="This bank account will be removed from the list. You can add it again later."
        confirmLabel="Delete"
        onConfirm={() => {
          const id = confirmDel.id;
          setConfirmDel({ open: false, id: null });
          if (id) removeBank(id);
        }}
        testid="confirm-del-bank"
      />

      <ConfirmDialog
        open={confirmDelSub.open}
        onOpenChange={(o) => setConfirmDelSub({ ...confirmDelSub, open: o })}
        title="Delete sub-division?"
        description="Sub-divisions with users still assigned cannot be deleted."
        confirmLabel="Delete"
        onConfirm={() => {
          const id = confirmDelSub.id;
          setConfirmDelSub({ open: false, id: null });
          if (id) doDelSub(id);
        }}
        testid="confirm-del-subdiv"
      />

      <ConfirmDialog
        open={confirmDelPreset.open}
        onOpenChange={(o) => setConfirmDelPreset({ ...confirmDelPreset, open: o })}
        title={`Hapus preset "${confirmDelPreset.label}"?`}
        description="Preset yang sudah dipakai staff existing tidak akan rusak — permission mereka tetap tersimpan. Preset hanya hilang dari daftar pilihan."
        confirmLabel="Delete"
        onConfirm={() => {
          const id = confirmDelPreset.id;
          setConfirmDelPreset({ open: false, id: null, label: "" });
          if (id) doDelPreset(id);
        }}
        testid="confirm-del-preset"
      />
    </div>
  );
}

/** Placeholder untuk tab Settings yang kategorinya sudah ada tapi kontennya menyusul. */
function SettingsPlaceholder({ icon: Icon, title, description }) {
  return (
    <section className="bg-white border border-slate-200 max-w-3xl" data-testid="settings-placeholder">
      <div className="p-10 text-center">
        <Icon size={26} className="mx-auto text-slate-300 mb-3" />
        <div className="text-sm font-semibold text-[#0B1D3A]">{title}</div>
        <div className="text-xs text-slate-500 mt-1.5 max-w-md mx-auto leading-relaxed">{description}</div>
        <div className="mt-4 inline-flex items-center gap-1 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 uppercase tracking-wider font-semibold">
          <Sparkles size={11} /> Segera Hadir
        </div>
        <div className="mt-2 text-[10px] text-slate-400 italic">Hanya terlihat oleh admin (BOD Lv1) — disembunyikan dari user lain.</div>
      </div>
    </section>
  );
}

function Field({ label, value, readonlyBadge }) {
  return (
    <div>
      <div className="overline text-slate-500">{label}</div>
      <div className="mt-1 text-[#0B1D3A] font-medium flex items-center gap-2">
        {value}
        {readonlyBadge && <span className="text-[10px] text-slate-400 uppercase tracking-wider bg-slate-100 px-1.5 py-0.5">{readonlyBadge}</span>}
      </div>
    </div>
  );
}

// 2026-09-24 (user request): pengaturan warna export Excel (Buku Bank /
// Estatement / Breakdown / Report Weekly Petty Cash).
const EXPORT_COLOR_DEFAULTS = { green: "92D050", purple: "C58BEF", yellow: "FFFF00", grey: "D9D9D9" };
const EXPORT_COLOR_LABELS = {
  green: "Hijau — Penerimaan / mutasi masuk (CREDIT)",
  purple: "Ungu — Pengeluaran / mutasi keluar (DEBIT)",
  yellow: "Kuning — Baris Saldo Awal",
  grey: "Abu-abu — Header kolom",
};

function ExportColorsSettings() {
  const [colors, setColors] = useState(EXPORT_COLOR_DEFAULTS);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    getExportColors().then((c) => { if (c) setColors({ ...EXPORT_COLOR_DEFAULTS, ...c }); }).catch(() => {});
  }, []);
  const save = async (vals) => {
    try {
      setBusy(true);
      const s = await saveExportColors(vals);
      if (s) setColors({ ...EXPORT_COLOR_DEFAULTS, ...s });
      toast.success("Warna export disimpan.");
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Gagal menyimpan warna.");
    } finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-slate-200 p-5 max-w-xl" data-testid="export-colors-panel">
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-700 flex items-center gap-1.5">
        <Palette size={13} className="text-[#0B1D3A]" /> Warna Export Excel
      </div>
      <p className="text-[11px] text-slate-500 mt-1">
        Berlaku untuk export Excel Buku Bank (Estatement &amp; Breakdown) dan Report Weekly Petty Cash.
      </p>
      <div className="mt-4 space-y-3">
        {Object.keys(EXPORT_COLOR_LABELS).map((k) => (
          <div key={k} className="flex items-center gap-3" data-testid={`export-color-row-${k}`}>
            <input
              type="color"
              value={`#${colors[k]}`}
              onChange={(e) => setColors({ ...colors, [k]: e.target.value.replace("#", "").toUpperCase() })}
              data-testid={`export-color-${k}`}
              className="w-10 h-8 border border-slate-300 cursor-pointer bg-white"
            />
            <div className="flex-1">
              <div className="text-[11px] font-semibold text-slate-700">{EXPORT_COLOR_LABELS[k]}</div>
              <div className="text-[10px] text-slate-400 tabular-nums">#{colors[k]}</div>
            </div>
            <div className="w-16 h-6 border border-slate-200" style={{ backgroundColor: `#${colors[k]}` }} />
          </div>
        ))}
      </div>
      <div className="mt-5 flex items-center gap-2">
        <Button onClick={() => save(colors)} disabled={busy} data-testid="export-colors-save"
          className="rounded-none bg-[#0B1D3A] hover:bg-[#12284d] text-white uppercase text-[10px] tracking-wider font-semibold h-8">
          {busy ? "Menyimpan…" : "Simpan"}
        </Button>
        <Button onClick={() => { setColors(EXPORT_COLOR_DEFAULTS); save(EXPORT_COLOR_DEFAULTS); }} disabled={busy}
          variant="outline" data-testid="export-colors-reset"
          className="rounded-none h-8 border border-slate-300 text-slate-700 hover:bg-slate-100 uppercase text-[10px] tracking-wider font-semibold">
          Reset Default
        </Button>
      </div>
    </div>
  );
}

